/**
 * The shapes the lesson screens draw progress from: the week strip, the next
 * step of today's list, a set's ring and a level's tally. Kept apart from the
 * views for `node --test`, as `today.ts` is.
 */

/** The method's week with one set (lesson library PRD §4.2). */
export const SET_DAYS = 7;

/** A `YYYY-MM-DD` local day moved by `n` days, by calendar fields, never through UTC. */
export function addDays(day: string, n: number): string {
  const [y, m, d] = day.split('-').map(Number);
  const at = new Date(y as number, (m as number) - 1, (d as number) + n);
  const pad = (v: number) => String(v).padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}

export type ActivityDay = { localDay: string; plays: number; completed: number };
export type StripDay = { localDay: string; state: 'none' | 'started' | 'done'; isToday: boolean };

/**
 * The seven days ending `today`, oldest first. "Done" is a day with a lesson
 * heard to the end, "started" one with only plays begun: a day the learner
 * pressed play counts for something, and a gap is just a gap, not a broken
 * streak.
 */
export function weekStrip(today: string, days: readonly ActivityDay[]): StripDay[] {
  const byDay = new Map(days.map((d) => [d.localDay, d]));
  return Array.from({ length: 7 }, (_, i) => {
    const localDay = addDays(today, i - 6);
    const d = byDay.get(localDay);
    const state = !d || d.plays === 0 ? 'none' : d.completed > 0 ? 'done' : 'started';
    return { localDay, state, isToday: i === 6 };
  });
}

/** Today's first step not yet done, in the list's order; null once every one is. */
export function nextStep<R extends { done: boolean }>(rows: readonly R[]): R | null {
  return rows.find((r) => !r.done) ?? null;
}

/** A set's ring: days played over the method's week, full and finished at seven. */
export function setProgress(daysPlayed: number): { fraction: number; finished: boolean } {
  return { fraction: Math.min(daysPlayed, SET_DAYS) / SET_DAYS, finished: daysPlayed >= SET_DAYS };
}

export function levelSummary(sets: readonly { daysPlayed: number }[]): { finished: number; total: number } {
  return { finished: sets.filter((s) => setProgress(s.daysPlayed).finished).length, total: sets.length };
}
